import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { ErrorType, PageType, SessionStorageName } from '@lib/interfaces';
import { CommonLibService, PageLoadingService, SystemDateService } from '@lib/services';
import {
  PostCheckinTravelersRequestContactsInner,
  PostCheckinTravelersRequestTravelersInner,
  PostJourneysGetJourneyResponseData,
  PostJourneysGetJourneyResponseDataFlightsInner,
  PostJourneysGetJourneyResponseDataTravelersInner,
} from 'src/sdk_checkin';
import { PREVIOUS_PAGE_IDENTIFIER } from '@conf/previous-page-identifier.config';
import {
  CheckinFunctionIdType,
  CheckinPageIdType,
  Contacts,
  NEXT_ACTION,
  ParameterToServicing,
  SITE_ID,
} from '../../interface';
import { PassengerInput } from '../../interface/checkin-passenger-input';
import { CheckinBaseClass } from '../../components';
import { CheckinCommonStoreService } from '../checkin-common-store/checkin-common-store.service';
import { CheckinPassengerInputResultStoreService } from './checkin-passenger-input-result-store.service';
import { CHECKIN_ROUTES } from '@conf/routes.config';
import { ServicingMybookingTransitionService } from '../servicing-mybooking-transition/servicing-mybooking-transition.service';
import { removeUndefinedOrNullProperties } from '@common/helpers';

@Injectable()
export class CheckinPassengerInputService extends CheckinBaseClass {
  /** チェックイン搭乗者情報更新APIレスポンス正常通知イベント */
  isInputPassengerSuccess$ = new Subject<boolean>();

  /** チェックイン搭乗者情報更新APIレスポンス継続不可能エラーメッセージコード */
  private static readonly ApiErrorMessageNotRetryable: { [errorCode: string]: string } = {
    EBAZ000319: 'E0801',
    EBAZ000647: 'E1811',
  };

  /** EBAZ000319用エラーメッセージコード「前画面IDが“checkinStatus”(チェックイン状況詳細画面)である」 */
  private static readonly ApiErrorMessageNotRetryableCheckInStatus: { [errorCode: string]: string } = {
    EBAZ000319: 'E1285',
  };

  /** チェックイン搭乗者情報更新APIレスポンス異常メッセージコード */
  private static readonly ApiErrorMessage: { [errorCode: string]: string } = {
    EBAZ000397: 'E0800',
    EBAZ000322: 'E1266',
    EBAZ000656: 'E1776',
  };

  /** どの画面からの遷移かを示す識別子 */
  get previousPageIdentifier(): string | undefined {
    return this._checkinCommonStoreService.checkinCommon.previousPageIdentifier;
  }

  /** 次画面遷移先URL */
  get nextPageUrl(): string {
    let url = '';

    switch (this.previousPageIdentifier) {
      case PREVIOUS_PAGE_IDENTIFIER.CHECKIN_SELECT:
        url = CHECKIN_ROUTES.seatmap;
        break;

      case PREVIOUS_PAGE_IDENTIFIER.CHECKIN_REVIEW:
        url = CHECKIN_ROUTES.checkinReview;
        break;

      case PREVIOUS_PAGE_IDENTIFIER.CHECKIN_STATUS:
        url = CHECKIN_ROUTES.checkinMybooking;
        break;

      case PREVIOUS_PAGE_IDENTIFIER.CHECKIN_SEATMAP:
        url = CHECKIN_ROUTES.seatmap;
        break;
    }

    return url;
  }

  /** チェックイン検索にて取得された、チェックイン対象journeyId */
  get journeyId(): string {
    return this._checkinCommonStoreService.checkinCommon.journeyId!;
  }

  /** システム日時 */
  get systemDate(): Date {
    return this._systemDateService.getSystemDate();
  }

  constructor(
    private _systemDateService: SystemDateService,
    private _commonLibService: CommonLibService,
    private _checkinCommonStoreService: CheckinCommonStoreService,
    private _checkinPassengerInutResultStoreService: CheckinPassengerInputResultStoreService,
    private _pageLoadingService: PageLoadingService,
    private _servicingMybookingTransitionService: ServicingMybookingTransitionService
  ) {
    super();
    this.watchPassengerInputSuccess();
    this.watchPassengerInputFailure();
  }

  override destroy(): void {
    this._checkinCommonStoreService.updateCheckinCommon({
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.CHECKIN_PASSENGER_INPUT,
    });
  }

  /**
   * チェックイン搭乗者情報変更操作時間内かを判定する
   * @param flights セグメント情報
   * @returns
   */
  getIsUpdateTravelersEligible(data: PostJourneysGetJourneyResponseData): boolean {
    let isUpdateTravelersEligible = true;

    if (this.previousPageIdentifier === PREVIOUS_PAGE_IDENTIFIER.CHECKIN_STATUS) {
      if (!data.isUpdateTravelersEligible) {
        isUpdateTravelersEligible = false;
      }
    }

    return isUpdateTravelersEligible;
  }

  /**
   * 搭乗者情報のリストの中で、以下の条件を全て満たす搭乗者が存在するか判定する
   * 　条件①：当該搭乗者.passengerTypeCode=”CHD”または”INF”または”INS”
   * 　条件②：当該搭乗者.regulatoryDetails.passportsの全ての要素についてbirthDateが存在しない、もしくは空欄
   * 　条件③：当該搭乗者.dateOfBirthが存在しない、もしくは空欄
   * @param travelers 搭乗者情報のリスト
   * @returns 搭乗者情報のリスト
   */
  getIsChildrenAndisNotSetBirthOfDay(travelers: PostJourneysGetJourneyResponseDataTravelersInner[]): boolean {
    for (let i = 0; i < travelers.length; i++) {
      const passengerTypeCode = travelers[i].passengerTypeCode;
      const passengerTypeCodeEnum = PostJourneysGetJourneyResponseDataTravelersInner.PassengerTypeCodeEnum;

      // travelers.passengerTypeCodeがCHD、INF、INSのいずれかである場合
      if (
        passengerTypeCode === passengerTypeCodeEnum.Chd ||
        passengerTypeCode === passengerTypeCodeEnum.Inf ||
        passengerTypeCode === passengerTypeCodeEnum.Ins
      ) {
        // パスポート情報
        const passports = travelers[i].regulatoryDetails?.passports;
        // パスポートが存在しない、もしくはパスポートの全てのレコードについて生年月日が存在しない
        const isNotExistPassportDateOfBirth = !passports || passports.every((passport) => !passport.birthDate);
        // NameElementの生年月日が存在しない
        const isNotExistNameElementDateOdBirth = !travelers[i].dateOfBirth;

        // パスポートにもNameElementにも生年月日が存在しなければtrueを返す
        if (isNotExistPassportDateOfBirth && isNotExistNameElementDateOdBirth) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * チェックイン状況詳細画面から遷移時にチェックイン済みの搭乗者リストを取得する
   * @param travelers 搭乗者情報のリスト
   * @returns 搭乗者情報のリスト
   */
  getCheckinTravelers(
    travelers: PostJourneysGetJourneyResponseDataTravelersInner[]
  ): PostJourneysGetJourneyResponseDataTravelersInner[] {
    let checkinTravelers = travelers;

    if (this.previousPageIdentifier === PREVIOUS_PAGE_IDENTIFIER.CHECKIN_STATUS) {
      checkinTravelers = checkinTravelers.filter(
        (traveler) =>
          traveler.acceptanceStatusSummary ===
            PostJourneysGetJourneyResponseDataTravelersInner.AcceptanceStatusSummaryEnum.Accepted ||
          traveler.acceptanceStatusSummary ===
            PostJourneysGetJourneyResponseDataTravelersInner.AcceptanceStatusSummaryEnum.Partial
      );
    }

    return checkinTravelers;
  }

  /**
   * チェックイン搭乗者情報更新APIリクエスト
   * @param passengerInputs チェックイン搭乗者情報
   */
  inputPassenger(passengerInputs: PassengerInput[]): void {
    const travelers = passengerInputs.map<PostCheckinTravelersRequestTravelersInner>((passengerInput) => {
      const travelerParameter = {
        travelerId: passengerInput.travelerId,
        regulatoryDetails: {
          passport: passengerInput.passport,
          secondaryDocuments: passengerInput.immigration.secondaryDocuments,
          redressNumber: passengerInput.immigration.redressNumber,
          knownTravelerNumber: passengerInput.immigration.knownTravelerNumber,
          residenceCountryCode: passengerInput.immigration.residenceCountryCode,
        },
        frequentFlyerCard: passengerInput.frequentFlyerCard,
      };
      // 搭乗者情報の中で、undefinedが設定されている項目を削除
      removeUndefinedOrNullProperties(travelerParameter);
      return travelerParameter;
    });

    const contacts = passengerInputs
      .filter((passengerInput) => {
        return passengerInput.notification?.email;
      })
      .map<PostCheckinTravelersRequestContactsInner>((passengerInput) => {
        const contact: PostCheckinTravelersRequestContactsInner = {
          travelerId: passengerInput.travelerId,
        };

        if (passengerInput.notification?.email) {
          contact.email = {
            address: passengerInput.notification.email.address,
          };
        }

        if (passengerInput.notification?.phone) {
          contact.phone = {
            countryPhoneExtension: passengerInput.notification.phone.countryPhoneExtension,
            number: passengerInput.notification.phone.number,
          };
        }

        if (passengerInput.notification?.emergencyContact) {
          contact.emergencyContact = {
            countryCode: passengerInput.notification.emergencyContact.countryCode,
            number: passengerInput.notification.emergencyContact.number,
            ownerNameOfPhone: passengerInput.notification.emergencyContact.ownerNameOfPhone,
          };
        }
        return contact;
      });

    this._checkinPassengerInutResultStoreService.request({
      journeyId: this.journeyId,
      travelers: travelers,
      contacts: contacts,
      lang: this._commonLibService.aswContextStoreService.aswContextData.lang,
    });
  }

  /**
   * チェックイン搭乗者情報更新APIレスポンス正常取得
   */
  private watchPassengerInputSuccess(): void {
    this.subscribeService(this._checkinPassengerInutResultStoreService.success$, (state) => {
      this.isInputPassengerSuccess$.next(true);
      // ローディング画面表示終了
      this._pageLoadingService.endLoading();
    });
  }

  /**
   * チェックイン搭乗者情報更新APIレスポンス異常取得
   */
  private watchPassengerInputFailure(): void {
    this.subscribeService(
      this._checkinPassengerInutResultStoreService.failure$.pipe(
        withLatestFrom(this._commonLibService.apiErrorResponseService.getApiErrorResponse$(), (_, state) => state)
      ),
      (state) => {
        const apiError = state?.errors?.[0];
        if (apiError) {
          const matchErrorMsgId = CheckinPassengerInputService.ApiErrorMessage[apiError.code];
          // 前画面情報取得
          const isCheckinStatus = this.previousPageIdentifier === PREVIOUS_PAGE_IDENTIFIER.CHECKIN_STATUS;
          // 継続不可能エラーであるかを引き当てる
          const matchErrorMsgIdNotRetryable =
            isCheckinStatus && apiError.code === 'EBAZ000319'
              ? CheckinPassengerInputService.ApiErrorMessageNotRetryableCheckInStatus[apiError.code]
              : CheckinPassengerInputService.ApiErrorMessageNotRetryable[apiError.code];
          if (matchErrorMsgId) {
            // 継続可能エラーの場合
            this._commonLibService.errorsHandlerService.setRetryableError(PageType.PAGE, {
              errorMsgId: matchErrorMsgId,
              apiErrorCode: apiError.code,
            });
          } else if (matchErrorMsgIdNotRetryable) {
            // 継続不可能エラーの場合
            // 継続不可能エラーのStoreに情報を格納する
            this._commonLibService.errorsHandlerService.setNotRetryableError({
              errorType: ErrorType.BUSINESS_LOGIC,
              errorMsgId: matchErrorMsgIdNotRetryable,
              apiErrorCode: apiError.code,
            });
          } else {
            // どれにも当てはまらないエラーの場合
            this._commonLibService.errorsHandlerService.setNotRetryableError({
              errorType: ErrorType.SYSTEM,
              apiErrorCode: apiError.code,
            });
          }
        } else {
          this.isInputPassengerSuccess$.next(false);
        }

        // ローディング画面表示終了
        this._pageLoadingService.endLoading();
      }
    );
  }

  /**
   * 予約詳細（搭乗者）に遷移する
   * @param travelers 予約詳細画面へ渡す搭乗者情報リストを作成するための搭乗者配列
   * @param orderId 0番目のセグメント、表示対象搭乗者リストの0番目の搭乗者に紐づくorderId
   */
  transitionServicingMybooking(
    travelers: PostJourneysGetJourneyResponseDataTravelersInner[] | undefined,
    orderId: string | undefined
  ) {
    if (!travelers || !orderId) {
      return;
    }

    // 遷移先に渡すパラメータの設定
    const param: ParameterToServicing = {
      searchType: 'order',
      orderId: orderId,
      firstName: this._commonLibService.aswServiceStoreService.aswServiceData.firstName,
      lastName: this._commonLibService.aswServiceStoreService.aswServiceData.lastName,
      SITE_ID: SITE_ID.ASW_APP,
      nextAction: NEXT_ACTION.PASSENGER_INPUT,
      JSessionId: sessionStorage.getItem(SessionStorageName.JSESSION_ID),
      JourneyId: this._checkinCommonStoreService.checkinCommon.journeyId,
      flightIdList: this._checkinCommonStoreService.checkinCommon.selectedFlightIds,
      travelers: this._servicingMybookingTransitionService.concatTravelerNames(travelers),
      previousScreenId: `${CheckinFunctionIdType.CHECKIN}-${CheckinPageIdType.CHECKIN_PASSENGER_INPUT}`,
    };
    this._servicingMybookingTransitionService.transitionToServicing(param);
  }

  /**
   * チェックイン搭乗者情報入力画面スキップ判定
   * チェックイン搭乗者情報入力画面スキップして良いかを判定して返却する
   * @return 遷移元画面がチェックイン選択かつ、チェックイン対象選択画面にて搭乗者情報入力画面がスキップ可と判定された場合true、上記以外の場合false
   */
  isCheckinPassengerInputSkip() {
    return (
      this._checkinCommonStoreService.checkinCommon.previousPageIdentifier ===
        PREVIOUS_PAGE_IDENTIFIER.CHECKIN_SELECT &&
      this._checkinCommonStoreService.checkinCommon.isSkipCheckinPassengerInput
    );
  }
}
